/**
 * ZEN ERP — Dashboard İşlem Bekleyen Siparişler
 * DashboardPage.jsx'ten çıkarıldı.
 */
import React from "react";
import { Truck, Pencil, Send, Wallet, CheckCircle2, Check } from "lucide-react";
import { fmt, ono } from "../../appUtils";
import { printHtmlLabelForOrder } from "../../utils/pdfUtils";

export function DashboardPendingOrders({
  summary,
  tr,
  setEditingOrder,
  onSetStatus,
  onMarkShip,
  onRequestPayment,
  onPartialPay,
  onMarkPaid,
}) {
  const totalPending = (summary.imalatta?.length || 0) + (summary.kargolanacak?.length || 0) + (summary.yollandi?.length || 0);
  if (totalPending === 0 && !(summary.imalataHazir?.length > 0)) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-extrabold tracking-tight text-slate-900 dark:text-slate-100">{tr("İşlem Bekleyen Siparişler")}</h3>

      {/* Onaylandı / İmalata Hazır */}
      {(summary.imalataHazir?.length > 0) && (
        <section>
          <h4 className="text-xs font-bold flex items-center gap-2 mb-2 uppercase tracking-wider text-emerald-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            {tr("Onaylandı / İmalata Hazır")} ({summary.imalataHazir.length})
          </h4>
          <div className="space-y-2">
            {summary.imalataHazir.slice(0, 5).map((o) => (
              <div key={o.id} className="zen-panel py-2.5 px-3 flex items-center gap-3 text-sm border-l-2 border-emerald-500" data-testid={`order-card-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="zen-mono text-xs font-bold text-emerald-600 mr-2">{ono(o)}</span>
                  <span className="text-slate-500 text-xs">{o.customerName}</span>
                  <div className="text-[11px] text-slate-500 truncate">{o.items?.[0]?.product || "—"}{o.items?.length > 1 && ` +${o.items.length - 1}`}</div>
                </div>
                <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">{fmt(o.total, o.currency)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* İmalatta */}
      {summary.imalatta?.length > 0 && (
        <section>
          <h4 className="text-xs font-bold flex items-center gap-2 mb-2 uppercase tracking-wider text-amber-500">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            {tr("İmalatta")} ({summary.imalatta.length})
          </h4>
          <div className="space-y-2">
            {summary.imalatta.slice(0, 5).map((o) => (
              <div key={o.id} className="zen-panel py-2.5 px-3 flex items-center gap-3 text-sm" data-testid={`order-card-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="zen-mono text-xs font-bold text-[#E5322A] mr-2">{ono(o)}</span>
                  <span className="text-slate-500 text-xs">{o.customerName}</span>
                  <div className="text-[11px] text-slate-500 truncate">{o.items?.[0]?.product || "—"}{o.items?.length > 1 && ` +${o.items.length - 1}`}</div>
                </div>
                <div className="zen-mono font-bold">{fmt(o.total, o.currency)}</div>
                <button data-testid={`btn-to-shipping-${o.id}`} onClick={() => onSetStatus(o, "kargolanacak")} className="zen-btn-ghost !py-1.5 !px-3 !text-xs">
                  <Truck className="w-3.5 h-3.5" /> {tr("Kargoya Hazır")}
                </button>
                <button data-testid={`btn-edit-${o.id}`} onClick={() => setEditingOrder(o)} className="zen-btn-ghost !py-1.5 !px-2">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Kargolanacak */}
      {summary.kargolanacak?.length > 0 && (
        <section>
          <h4 className="text-xs font-bold flex items-center gap-2 mb-2 uppercase tracking-wider text-sky-500">
            <span className="w-2 h-2 rounded-full bg-sky-500" />
            {tr("Kargolanacak")} ({summary.kargolanacak.length})
          </h4>
          <div className="space-y-2">
            {summary.kargolanacak.slice(0, 5).map((o) => (
              <div key={o.id} className="zen-panel py-2.5 px-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <span className="zen-mono text-xs font-bold text-[#E5322A] mr-2">{ono(o)}</span>
                  <span className="text-slate-500 text-xs">{o.customerName}</span>
                </div>
                <div className="zen-mono font-bold">{fmt(o.total, o.currency)}</div>
                <button data-testid={`btn-cargo-label-dash-${o.id}`} onClick={() => printHtmlLabelForOrder(o, "kargo")} className="zen-btn-ghost !py-1.5 !px-2 !text-xs" title="Kargo Etiketi Yazdır">
                  <Truck className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`btn-ship-${o.id}`} onClick={() => onMarkShip(o)} className="zen-btn-primary !py-1.5 !px-3 !text-xs">
                  <Truck className="w-3.5 h-3.5" /> {tr("Yollandı")}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Ödeme Bekliyor */}
      {summary.yollandi?.length > 0 && (
        <section>
          <h4 className="text-xs font-bold flex items-center gap-2 mb-2 uppercase tracking-wider text-orange-500">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            {tr("Ödeme Bekliyor")} ({summary.yollandi.length})
          </h4>
          <div className="space-y-2">
            {summary.yollandi.slice(0, 5).map((o) => (
              <div key={o.id} className="zen-panel py-2.5 px-3 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <span className="zen-mono text-xs font-bold text-[#E5322A] mr-2">{ono(o)}</span>
                  <span className="text-slate-500 text-xs">{o.customerName}</span>
                </div>
                <div className="text-right">
                  <div className="zen-mono font-bold">{fmt(o.total, o.currency)}</div>
                  {(o.paidAmount || 0) > 0 && (
                    <div className="text-[10px] text-amber-500 zen-mono">{tr("Kalan")}: {fmt((o.total || 0) - (o.paidAmount || 0), o.currency)}</div>
                  )}
                </div>
                <button data-testid={`btn-req-pay-${o.id}`} onClick={() => onRequestPayment(o)} className="zen-btn-ghost !py-1.5 !px-3 !text-xs">
                  <Send className="w-3.5 h-3.5" /> {tr("Ödeme İste")}
                </button>
                <button data-testid={`btn-partial-pay-${o.id}`} onClick={() => onPartialPay(o)} className="zen-btn-ghost !py-1.5 !px-3 !text-xs !border-amber-500/50 !text-amber-600 dark:!text-amber-400">
                  <Wallet className="w-3.5 h-3.5" /> {tr("Kısmi Ödeme")}
                </button>
                <button data-testid={`btn-paid-${o.id}`} onClick={() => onMarkPaid(o)} className="zen-btn-primary !py-1.5 !px-3 !text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {tr("Ödendi")}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
